/**
 * `last_updated` → **Settings** (spec M1-B5 §6.2, M2 tier-1 §5.10, card P-123).
 *
 * The same two controls as `creation_log`, over the same two keys, because
 * `lastUpdatedSettingsSchema` declares the same pair — `mode` by analogy with `creation_log`,
 * which is recorded as **ASSUMED** in the shared folder (Q72) and is repeated here rather than
 * silently taken for measured. The captured `Settings` submenu
 * (`editors/last_updated/settings-menu.json`) has no *Customize* row.
 *
 * The column is projected from `items.updated_at` + `items.updated_by` and is never written, so
 * every control here is display-only; `updater_id` is null when an automation made the change.
 */
import type { LastUpdatedSettings } from '@elizmonday/shared';
import { HideSummaryField, SegmentedField, SettingsForm, useSettingsDraft } from '../settings-kit';
import type { ColumnSettingsProps } from '../types';

const MODES = [
  { id: 'personAndDate', label: 'Person and date', value: 'personAndDate' },
  { id: 'personOnly', label: 'Person', value: 'personOnly' },
  { id: 'dateOnly', label: 'Date', value: 'dateOnly' },
] as const;

const DATE_FORMATS = [
  { id: 'dateFormat', label: 'Date', value: 'dateFormat' as const },
  { id: 'dateAgo', label: 'Time ago', value: 'dateAgo' as const },
];

export function LastUpdatedSettingsDialog({
  settings,
  onCommit,
  onCancel,
}: ColumnSettingsProps<LastUpdatedSettings>) {
  const [draft, patch] = useSettingsDraft<LastUpdatedSettings>(settings);
  return (
    <SettingsForm
      title="Last Updated column settings"
      testId="last-updated-settings"
      onSave={() => onCommit(draft)}
      onCancel={onCancel}
    >
      <SegmentedField
        label="Show"
        testId="last-updated-mode"
        value={draft.mode ?? 'personAndDate'}
        options={MODES}
        onChange={(next) => patch({ mode: next })}
      />
      <SegmentedField
        label="Date format"
        testId="last-updated-date-format"
        value={draft.dateFormatMode ?? 'dateFormat'}
        options={DATE_FORMATS}
        onChange={(next) => patch({ dateFormatMode: next })}
      />
      <HideSummaryField
        testId="last-updated-hide-footer"
        checked={draft.hide_footer ?? false}
        onChange={(next) => patch({ hide_footer: next })}
      />
    </SettingsForm>
  );
}
